import { DitherBackground } from "@redotech/redo-web/login/dither-background";
import classNames from "classnames";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LandingHeader } from "../components/layouts/landing-header";
import * as shellStyles from "../components/layouts/onboarding-shell.module.css";
import * as layoutStyles from "../components/layouts/two-column-layout.module.css";
import { DecryptedText } from "../components/ui/decrypted-text";
import { TiltCard } from "../components/ui/tilt-card";
import { getDashboardUrl } from "../core/auth";
import { useAuth } from "../core/auth-provider";
import { DITHER_COLORS, useDarkMode } from "../hooks/use-dark-mode";
import { useMerchantInfo } from "../hooks/use-merchant-info";
import { getProductsForFeatureFlags, PRODUCTS } from "../products";
import * as styles from "./landing-page.module.css";
import { LandingSkeleton } from "./landing-skeleton";

export const LandingPage = memo(function LandingPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { merchantInfo, enabledProducts, featureFlags } = useMerchantInfo();
  const loadedCount = useRef(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const handleImageLoad = useCallback(() => {
    loadedCount.current += 1;
    if (loadedCount.current >= PRODUCTS.length) {
      setImagesLoaded(true);
    }
  }, []); // PRODUCTS.length is stable (static array length never changes)
  const storeName = merchantInfo?.storeName;
  const isDark = useDarkMode();
  const ditherColors = isDark ? DITHER_COLORS.dark : DITHER_COLORS.light;

  const products = useMemo(
    () => getProductsForFeatureFlags(featureFlags),
    [featureFlags],
  );

  const { sortedProducts, activeCount } = useMemo(() => {
    const sorted = [...products].sort((a, b) => {
      const aActive = enabledProducts?.[a.id] ? 1 : 0;
      const bActive = enabledProducts?.[b.id] ? 1 : 0;
      return aActive - bActive;
    });
    const count = enabledProducts
      ? Object.values(enabledProducts).filter(Boolean).length
      : 0;
    return { sortedProducts: sorted, activeCount: count };
  }, [products, enabledProducts]);

  const handleProductClick = useCallback(
    (productId: string) => {
      if (enabledProducts?.[productId]) {
        window.location.href = getDashboardUrl(auth?.token);
        return;
      }
      const product = sortedProducts.find((p) => p.id === productId);
      if (product?.externalRedirectPath) {
        window.location.href = getDashboardUrl(
          auth?.token,
          product.externalRedirectPath,
        );
        return;
      }
      navigate(`/${productId}${window.location.search}`, {
        state: { from: "/" },
      });
    },
    [navigate, enabledProducts, auth, sortedProducts],
  );

  return (
    <>
      {!imagesLoaded && (
        <div className={shellStyles.shell}>
          <LandingSkeleton />
        </div>
      )}
      <div
        className={shellStyles.shell}
        style={
          imagesLoaded
            ? undefined
            : { position: "absolute", opacity: 0, pointerEvents: "none" }
        }
      >
        <LandingHeader
          hasActiveProduct={activeCount > 0}
          logoUrl={merchantInfo?.logoUrl}
          storeName={storeName}
        />
        <div className={layoutStyles.layout}>
          <div className={layoutStyles.leftColumn}>
            <div
              className={classNames(layoutStyles.leftInner, styles.leftInner)}
            >
              <div className={styles.spacer} />
              <h1 className={styles.title}>
                {storeName ? (
                  <>
                    {"Welcome, "}
                    {imagesLoaded ? (
                      <DecryptedText
                        sequential
                        speed={60}
                        text={`${storeName}!`}
                      />
                    ) : (
                      `${storeName}!`
                    )}
                  </>
                ) : (
                  "Welcome to Redo"
                )}
              </h1>
              <p className={styles.subtitle}>
                Which product would you like to set up?
              </p>
              <div className={styles.spacer} />
              {enabledProducts && (
                <p className={styles.counter}>
                  {activeCount} of {PRODUCTS.length} products active
                </p>
              )}
            </div>
          </div>

          <div className={layoutStyles.rightColumn}>
            <DitherBackground
              backgroundColor={ditherColors.bg}
              waveColor={ditherColors.wave}
            />
            <div className={layoutStyles.rightInner}>
              <div className={layoutStyles.grid}>
                {sortedProducts.map((product) => (
                  <TiltCard
                    active={enabledProducts?.[product.id] ?? false}
                    className={styles.card!}
                    image={product.imageUrl}
                    key={product.id}
                    label={product.label}
                    onClick={() => handleProductClick(product.id)}
                    onImageLoad={handleImageLoad}
                    selfServe={product.selfServe}
                    subtext={product.subtext}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
});
